import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import NotificationBell from './NotificationBell';
import logoFull from '../../assets/brand/logo-full-trimmed.png';

const MenuIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="3" y1="6" x2="21" y2="6" />
        <line x1="3" y1="12" x2="21" y2="12" />
        <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
);

const CloseIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);

const LogOutIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
        <polyline points="16 17 21 12 16 7" />
        <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
);

const Header = () => {
    const [mobileOpen, setMobileOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    const onContractorRoute = location.pathname.startsWith('/contractor');
    const onCustomerRoute   = location.pathname.startsWith('/customer');
    const customerId        = localStorage.getItem('customer_id');
    const contractorId      = localStorage.getItem('contractor_id');
    const isContractor      = onContractorRoute && !!contractorId;
    const isCustomer        = onCustomerRoute   && !!customerId;

    const customerLinks = [
        { label: 'ホーム',         path: '/customer/dashboard' },
        { label: '進行状況',       path: '/customer/progress' },
        { label: '業者を探す',     path: '/customer/search' },
        { label: 'アカウント',     path: '/customer/account' },
    ];
    const contractorLinks = [
        { label: 'ホーム',         path: '/contractor/dashboard' },
        { label: 'やることリスト', path: '/contractor/progress' },
        { label: '書類管理',       path: '/contractor/documents' },
        { label: 'プロフィール',   path: '/contractor/profile' },
    ];
    const links = isCustomer ? customerLinks : isContractor ? contractorLinks : [];

    const isActive = (path) => location.pathname === path;

    // 顧客/業者どちらのトップページを見ているかの切り替えピル（未ログイン・トップページ限定で表示）
    const modeTabs = [
        { label: 'お客様の方',   path: '/' },
        { label: '施工業者の方', path: '/contractor/register' },
    ];
    const isTopPage = location.pathname === '/' || location.pathname === '/contractor/register';
    const showModeToggle = isTopPage && !isCustomer && !isContractor;

    const ModeToggle = ({ compact }) => (
        <div style={{ display: 'flex', background: '#f5f6f8', borderRadius: 20, padding: 3, gap: 2, width: compact ? '100%' : 'auto' }}>
            {modeTabs.map(tab => {
                const active = location.pathname === tab.path;
                return (
                    <Link
                        key={tab.path}
                        to={tab.path}
                        onClick={() => setMobileOpen(false)}
                        style={{
                            flex: compact ? 1 : 'none',
                            textAlign: 'center',
                            padding: compact ? '8px 10px' : '6px 14px',
                            borderRadius: 16,
                            fontSize: compact ? '0.85rem' : '0.82rem',
                            fontWeight: 600,
                            textDecoration: 'none',
                            transition: 'background 0.15s, color 0.15s',
                            background: active ? '#005c53' : 'transparent',
                            color: active ? '#fff' : '#6b7280',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {tab.label}
                    </Link>
                );
            })}
        </div>
    );

    const handleLogout = () => {
        if (isContractor) {
            localStorage.removeItem('contractor_id');
            localStorage.removeItem('company_name');
            localStorage.removeItem('contractor_access_token');
        } else {
            localStorage.removeItem('customer_id');
            localStorage.removeItem('user_id');
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
        }
        setMobileOpen(false);
        navigate('/');
    };

    const logoPath = isCustomer ? '/customer/dashboard' : isContractor ? '/contractor/dashboard' : '/';

    return (
        <header className="no-print" style={{ position: 'sticky', top: 0, zIndex: 50, background: '#fff', borderBottom: '1px solid #e0e4ea' }}>
            <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 24px', height: 64, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>

                {/* ロゴ */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
                    <Link to={logoPath} style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
                        <img src={logoFull} alt="のりいれ三丁目" style={{ height: 32, display: 'block' }} />
                    </Link>
                    {isContractor && (
                        <span style={{ fontSize: '0.6rem', fontWeight: 700, color: '#9ca3af', letterSpacing: '0.08em', lineHeight: 1 }}>
                            CONTRACTOR
                        </span>
                    )}
                </div>

                {/* デスクトップナビ */}
                <div className="d-none d-md-flex" style={{ alignItems: 'center', gap: 32 }}>
                    <div style={{ display: 'flex', gap: 24 }}>
                        {links.map(link => (
                            <Link
                                key={link.path}
                                to={link.path}
                                style={{
                                    fontSize: '0.94rem',
                                    color: isActive(link.path) ? '#005c53' : '#6b7280',
                                    fontWeight: isActive(link.path) ? 600 : 400,
                                    textDecoration: 'none',
                                    paddingBottom: 2,
                                    borderBottom: isActive(link.path) ? '2px solid #005c53' : '2px solid transparent',
                                    transition: 'color 0.15s',
                                }}
                            >
                                {link.label}
                            </Link>
                        ))}
                    </div>

                    {/* 顧客/業者トップページ切り替え */}
                    {showModeToggle && <ModeToggle />}

                    {/* 通知ベル */}
                    {(isCustomer || isContractor) && (
                        <NotificationBell
                            userType={isContractor ? 'contractor' : 'customer'}
                            userId={isContractor ? Number(contractorId) : Number(customerId)}
                        />
                    )}

                    {/* ログイン済みのログアウトボタン */}
                    {(isCustomer || isContractor) && (
                        <button
                            onClick={handleLogout}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 6,
                                padding: '6px 12px', background: 'none',
                                border: 'none', cursor: 'pointer',
                                color: '#6b7280', fontSize: '0.875rem', borderRadius: 6,
                            }}
                        >
                            <LogOutIcon /> ログアウト
                        </button>
                    )}

                    {/* 未ログイン */}
                    {!isCustomer && !isContractor && (
                        <div style={{ display: 'flex', gap: 12 }}>
                            <Link
                                to="/customer/login"
                                style={{ fontSize: '0.9rem', color: '#6b7280', textDecoration: 'none', padding: '6px 12px' }}
                            >
                                顧客ログイン
                            </Link>
                            <Link
                                to="/contractor/login"
                                style={{
                                    fontSize: '0.9rem', color: '#fff', textDecoration: 'none',
                                    padding: '6px 16px', background: '#005c53', borderRadius: 4, fontWeight: 500,
                                }}
                            >
                                業者ログイン
                            </Link>
                        </div>
                    )}
                </div>

                {/* モバイル：ベル＋ハンバーガー */}
                <div className="d-md-none" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    {(isCustomer || isContractor) && (
                        <NotificationBell
                            userType={isContractor ? 'contractor' : 'customer'}
                            userId={isContractor ? Number(contractorId) : Number(customerId)}
                        />
                    )}
                    <button
                        onClick={() => setMobileOpen(!mobileOpen)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 8, color: '#1a1a2e', borderRadius: 6 }}
                    >
                        {mobileOpen ? <CloseIcon /> : <MenuIcon />}
                    </button>
                </div>
            </div>

            {/* モバイルメニュー（アコーディオン） */}
            <div style={{
                maxHeight: mobileOpen ? 400 : 0,
                overflow: 'hidden',
                transition: 'max-height 0.28s ease',
                borderTop: mobileOpen ? '1px solid #e0e4ea' : 'none',
                background: '#fff',
            }}>
                <div style={{ padding: '8px 16px 16px' }}>
                    {/* 顧客/業者トップページ切り替え（モバイル） */}
                    {showModeToggle && (
                        <div style={{ marginBottom: 12 }}>
                            <ModeToggle compact />
                        </div>
                    )}

                    {links.map(link => (
                        <Link
                            key={link.path}
                            to={link.path}
                            onClick={() => setMobileOpen(false)}
                            style={{
                                display: 'block',
                                padding: '12px 16px',
                                borderRadius: 6,
                                marginBottom: 2,
                                color: isActive(link.path) ? '#005c53' : '#6b7280',
                                fontWeight: isActive(link.path) ? 600 : 400,
                                background: isActive(link.path) ? 'rgba(0,92,83,0.07)' : 'transparent',
                                textDecoration: 'none',
                                fontSize: '0.95rem',
                            }}
                        >
                            {link.label}
                        </Link>
                    ))}

                    {/* 未ログイン（モバイル） */}
                    {!isCustomer && !isContractor && (
                        <>
                            <Link to="/customer/login" onClick={() => setMobileOpen(false)}
                                style={{ display: 'block', padding: '12px 16px', borderRadius: 6, marginBottom: 2, color: '#6b7280', textDecoration: 'none', fontSize: '0.95rem' }}>
                                顧客ログイン
                            </Link>
                            <Link to="/contractor/login" onClick={() => setMobileOpen(false)}
                                style={{ display: 'block', padding: '12px 16px', borderRadius: 6, color: '#005c53', fontWeight: 500, textDecoration: 'none', fontSize: '0.95rem' }}>
                                業者ログイン
                            </Link>
                        </>
                    )}

                    {/* ログアウト（モバイル） */}
                    {(isCustomer || isContractor) && (
                        <button
                            onClick={handleLogout}
                            style={{
                                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                padding: '12px 16px', marginTop: 8,
                                borderTop: '1px solid #e0e4ea', paddingTop: 14,
                                background: 'none', border: 'none', cursor: 'pointer',
                                color: '#6b7280', fontSize: '0.9rem', borderRadius: 0,
                            }}
                        >
                            <span>ログアウト</span>
                            <LogOutIcon />
                        </button>
                    )}
                </div>
            </div>
        </header>
    );
};

export default Header;
